import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { useUpcomingEvents } from "@/hooks/useUpcomingEvents";
import { HeroSlideshow } from "@/components/shared/HeroSlideshow";
import { AnimatedHeadline } from "@/components/shared/AnimatedHeadline";
import { Reveal, RevealStagger, RevealItem } from "@/components/shared/Reveal";
import { StatStrip } from "@/components/shared/StatStrip";
import { ChapterHeading } from "@/components/shared/ChapterHeading";
import { IconTile } from "@/components/shared/IconTile";
import {
  HERO_SLIDES, DISCOVERY_CATEGORIES, UPCOMING_EDITION, COLORS, STORY_IMAGE,
  DISCOVERY_BANNER, STATS_BANNER, SELL_SECTION_IMAGE, UPCOMING_CARD_IMAGE, CLOSING_BANNER,
} from "@/data/siteData";

export default function Home() {
  const { dates, dateRange } = useUpcomingEvents();
  useSEO(
    "The Market Place | Jaipur's Community-Led Marketplace for Homegrown Businesses",
    "Jaipur's community-led marketplace since 2019. 400+ markets, 1,000+ homegrown businesses. Book a stall or discover local makers near you."
  );

  const scrollToStory = () => {
    document.getElementById("our-story")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div data-testid="home-page">
      {/* Hero Slideshow */}
      <HeroSlideshow images={HERO_SLIDES}>
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="max-w-2xl">
            <span className="inline-block font-mono text-xs uppercase tracking-[0.25em] text-[#F48FB1] mb-6">
              Since 2019 &middot; Jaipur
            </span>
            <AnimatedHeadline
              lines={["To Jaipur.", "From Jaipur."]}
              className="font-extrabold tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-white"
            />
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.7 }}
              className="mt-6 text-xl sm:text-2xl font-semibold text-white max-w-xl leading-snug"
            >
              A community-led platform for Jaipur's homegrown businesses.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.7 }}
              className="mt-4 text-base sm:text-lg text-white/80 max-w-lg leading-relaxed"
            >
              Since 2019, The Market Place has brought together entrepreneurs, makers, independent businesses and
              customers through markets built around discovery, connection and growth.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.7 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <button
                onClick={scrollToStory}
                data-testid="hero-explore-button"
                className="rounded-full bg-[#D81B60] text-white px-8 py-4 text-sm font-semibold hover:bg-[#AD144B] hover:scale-[1.02] transition-all duration-300 shadow-lg flex items-center gap-2"
              >
                Explore The Market Place <ArrowDown size={16} />
              </button>
              <Link
                to="/book-a-stall"
                data-testid="hero-apply-stall-button"
                className="rounded-full border-2 border-white text-white px-8 py-4 text-sm font-semibold hover:bg-white hover:text-[#1A1A1A] transition-colors duration-300"
              >
                Book a Stall
              </Link>
            </motion.div>
          </div>
        </div>
      </HeroSlideshow>

      {/* A Marketplace Jaipur Has Grown With */}
      <section id="our-story" className="py-16 sm:py-20 px-6 sm:px-8 max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
        <Reveal>
          <div className="rounded-3xl overflow-hidden">
            <img src={STORY_IMAGE} alt="Homegrown businesses at The Market Place, Jaipur" className="w-full aspect-[4/5] object-cover object-top" />
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#D81B60]">Our Story</span>
          <h2 className="mt-4 font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight text-[#1A1A1A]">
            A Marketplace Jaipur Has Grown With
          </h2>
          <p className="mt-8 text-lg text-gray-600 leading-relaxed">
            The Market Place began with a simple idea: give homegrown businesses a meaningful space to meet their customers.
          </p>
          <p className="mt-5 text-lg text-gray-600 leading-relaxed">
            What started with organic farmers and independent organic brands has grown into a wider community of
            entrepreneurs across fashion, food, lifestyle, gifting, home, beauty, sustainable products and emerging local concepts.
          </p>
          <p className="mt-5 text-lg text-gray-600 leading-relaxed">
            Today, The Market Place continues to create spaces where businesses can be discovered, relationships can
            be built and Jaipur can support what is being created within Jaipur.
          </p>
        </Reveal>
      </section>

      {/* Numbers — full-bleed photo band */}
      <section className="relative py-16 sm:py-20 px-6 sm:px-8 overflow-hidden">
        <div className="absolute inset-0">
          <img src={STATS_BANNER} alt="A busy evening at The Market Place, Jaipur" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-black/70" />
        </div>
        <div className="relative z-10">
          <p className="text-center font-mono text-xs uppercase tracking-[0.25em] text-white/60 mb-8">The Market Place in Numbers</p>
          <StatStrip dark />
        </div>
      </section>

      {/* More Than a Place to Sell */}
      <section className="py-16 sm:py-20 px-6 sm:px-8 max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
        <Reveal className="order-2 md:order-1">
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#D81B60]">Building Jaipur's Homegrown Business Community</span>
          <h2 className="mt-4 font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight text-[#1A1A1A]">
            More Than a Place to Sell
          </h2>
          <p className="mt-6 text-lg text-gray-600 leading-relaxed">
            A market can give a business a stall. We believe a platform should give it much more.
          </p>
          <p className="mt-4 text-lg text-gray-600 leading-relaxed">
            The Market Place creates opportunities for entrepreneurs to meet customers directly, understand their
            market, build visibility, form collaborations and become part of a community that grows together.
          </p>
          <p className="mt-6 font-bold text-xl text-[#D81B60]">A community to grow in, not just a stall to sell from.</p>
          <Link
            to="/book-a-stall"
            data-testid="home-become-part-button"
            className="inline-block mt-8 rounded-full bg-[#D81B60] text-white px-8 py-4 text-sm font-semibold hover:bg-[#AD144B] transition-colors duration-300"
          >
            Become Part of The Market Place
          </Link>
        </Reveal>
        <Reveal delay={0.1} className="order-1 md:order-2">
          <div className="rounded-3xl overflow-hidden">
            <img src={SELL_SECTION_IMAGE} alt="A founder engaging with customers at The Market Place, Jaipur" className="w-full aspect-[4/5] object-cover object-top" />
          </div>
        </Reveal>
      </section>

      {/* Built for Discovery */}
      <section className="py-16 sm:py-20 px-6 sm:px-8 max-w-7xl mx-auto">
        <ChapterHeading number="01" title="Built for Discovery" subtitle="Every edition brings together a diverse mix of businesses, founders and ideas." />
        <RevealStagger className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-5">
          {DISCOVERY_CATEGORIES.map((c, i) => (
            <RevealItem key={c.title}>
              <div
                data-testid={`discovery-category-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="rounded-2xl border border-gray-200 p-6 h-full hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                style={{ borderColor: `${COLORS[i % COLORS.length]}30` }}
              >
                <IconTile icon={c.icon} title={c.title} color={COLORS[i % COLORS.length]} />
              </div>
            </RevealItem>
          ))}
        </RevealStagger>
        <Reveal>
          <p className="text-center mt-10 text-gray-500 italic">The mix changes. The spirit remains the same.</p>
        </Reveal>
      </section>

      {/* Photo banner */}
      <Reveal>
        <section className="px-6 sm:px-8 max-w-7xl mx-auto mb-12">
          <div className="rounded-3xl overflow-hidden">
            <img src={DISCOVERY_BANNER} alt="Shoppers and founders at The Market Place, Jaipur" className="w-full h-[45vh] sm:h-[55vh] object-cover object-top" />
          </div>
        </section>
      </Reveal>

      {/* Upcoming Market highlight */}
      <section className="py-16 sm:py-20 px-6 sm:px-8 max-w-7xl mx-auto">
        <ChapterHeading number="02" title="See You at the Next Market" subtitle={UPCOMING_EDITION.teaser} />
        <Reveal>
          <div data-testid="upcoming-market-card" className="rounded-3xl bg-gradient-to-br from-[#FDF2F5] to-[#FDFBF7] border border-gray-100 overflow-hidden grid md:grid-cols-2 items-stretch">
            <div className="p-8 sm:p-12 flex flex-col justify-center">
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#D81B60]">{dateRange}</span>
              <h3 className="font-extrabold text-3xl sm:text-4xl mt-3">{UPCOMING_EDITION.name}</h3>
              <div className="mt-6 flex flex-wrap gap-6 text-sm text-gray-600 font-mono">
                <span>{UPCOMING_EDITION.timing}</span>
                <span>{UPCOMING_EDITION.entry}</span>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-6">
                {dates.map((d, i) => (
                  <div key={d} className="rounded-xl bg-white border border-gray-200 py-3 text-center text-sm font-mono font-semibold" style={{ color: COLORS[i % COLORS.length] }}>
                    {d}
                  </div>
                ))}
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/upcoming-market" data-testid="home-see-details-button" className="inline-flex items-center gap-2 rounded-full bg-[#D81B60] text-white px-6 py-3 text-sm font-semibold hover:bg-[#AD144B] transition-colors duration-300">
                  View Upcoming Markets <ArrowRight size={16} />
                </Link>
                <Link to="/book-a-stall" data-testid="home-apply-stall-secondary-button" className="inline-flex items-center gap-2 rounded-full border border-gray-300 px-6 py-3 text-sm font-semibold hover:border-[#1A1A1A] transition-colors duration-300">
                  Book a Stall
                </Link>
              </div>
            </div>
            <div className="min-h-[280px]">
              <img src={UPCOMING_CARD_IMAGE} alt="The next edition of The Market Place, Jaipur" className="w-full h-full object-cover object-top" />
            </div>
          </div>
        </Reveal>
      </section>

      {/* Closing — full bleed */}
      <section className="relative py-20 sm:py-24 px-6 sm:px-8 overflow-hidden">
        <div className="absolute inset-0">
          <img src={CLOSING_BANNER} alt="The community at The Market Place, Jaipur" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-black/30" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <Reveal>
            <h2 className="font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight text-white">
              What Jaipur Creates Deserves to Be Discovered.
            </h2>
            <p className="mt-6 text-lg text-white/80 leading-relaxed max-w-2xl mx-auto">
              The Market Place continues to bring entrepreneurs and customers together, one market, one conversation and one business at a time.
            </p>
            <p className="mt-6 font-extrabold text-2xl text-[#F48FB1]">To Jaipur. From Jaipur.</p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
